/**
 * Reset Password — set a new password using a token from the reset email.
 *
 * The token is read from the ?token= query string. The backend validates
 * single-use, expiry, and rotates the password hash in one transaction.
 */

import React, { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { confirmPasswordReset } from "../api/client";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-account");
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError(t("ResetPasswordPage.missingTokenError"));
      return;
    }
    if (password.length < 8) {
      setError(t("ResetPasswordPage.tooShortError"));
      return;
    }
    if (password !== confirm) {
      setError(t("ResetPasswordPage.mismatchError"));
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset({ token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err: unknown) {
      // A 422 returns `detail` as an array of Pydantic error objects
      // ({type, loc, msg, input, ctx}); assigning it straight to `error`
      // rendered a raw object as a React child and crashed the page (Sentry
      // JAVASCRIPT-REACT-4). Route through the shared helper, which always
      // yields a display-safe string — same as Login/ForgotPassword.
      setError(toUserSafeErrorMessage(err, t("ResetPasswordPage.resetFailedError")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" component="h1" fontWeight={500} gutterBottom>
            {t("ResetPasswordPage.heading")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("ResetPasswordPage.subheading")}
          </Typography>

          {done ? (
            <Alert severity="success">
              {t("ResetPasswordPage.successMessage")}
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                  label={t("ResetPasswordPage.newPasswordLabel")}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  helperText={t("ResetPasswordPage.minimumCharsHelper")}
                />
                <TextField
                  label={t("ResetPasswordPage.confirmPasswordLabel")}
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                  sx={{ py: 1.5 }}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("ResetPasswordPage.updatePasswordButton")
                  )}
                </Button>
              </Stack>
            </form>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 3 }}
          >
            <Link component={RouterLink} to="/login" variant="body2">
              {t("ResetPasswordPage.backToSignInLink")}
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
